import { euro } from "../../lib/format";
import type { Purchase } from "../../lib/finance";

export interface Slice {
  label: string;
  value: number;
  color: string;
}

/**
 * A stacked bar over a legend of the same parts.
 *
 * A bar rather than a pie: these splits are wildly unequal — the loan is four
 * fifths of a monthly payment, the transfer duties four fifths of the fees —
 * and a bar keeps the small parts legible where a pie collapses them into
 * slivers. The legend carries the exact figures, so the bar only has to convey
 * proportion.
 */
export function Breakdown({ slices }: { slices: Slice[] }) {
  const parts = slices.filter((s) => s.value > 0);
  const total = parts.reduce((sum, p) => sum + p.value, 0);
  if (total <= 0) return null;

  return (
    <div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full">
        {parts.map((p) => (
          <span
            key={p.label}
            // A floor of two pixels: home insurance is under 1% of a monthly
            // payment and would otherwise render as nothing at all, making the
            // bar disagree with the legend beneath it.
            style={{
              width: `${(p.value / total) * 100}%`,
              minWidth: "2px",
              background: p.color,
            }}
            title={`${p.label} — ${euro(p.value)}`}
          />
        ))}
      </div>
      <ul className="mt-2 space-y-0.5">
        {parts.map((p) => (
          <li key={p.label} className="flex items-center gap-2 text-[11px]">
            <span
              className="h-2 w-2 shrink-0 rounded-sm ring-1 ring-inset ring-ink/10"
              style={{ background: p.color }}
            />
            <span className="flex-1 truncate text-ink-soft">{p.label}</span>
            <span className="tabular-nums">{euro(p.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Each card gets its own hue family, so a bar always says which card it came
// from before you have read a single label.

/** Monthly outgoings — blue for the loan, warmer tones for what sits on top. */
const MONTHLY = ["#2f6df6", "#7aa3f8", "#f6a445", "#cf3a2e", "#8a94a6"];
// Six steps, so the ramp stays saturated to the end rather than fading out —
// the last one still has to read as a colour in an 8px swatch on white.
/** One-off acquisition fees, in violet: paid once, never again. */
const FEES = ["#3d2f80", "#5a45a8", "#7a5fc9", "#957ad9", "#ab93e4", "#c3aeee"];
/** Recurring charges of ownership, in teal. */
const RUNNING = ["#0f766e", "#2fa397", "#7fcdc3"];

export function MonthlyBreakdown({ purchase }: { purchase: Purchase }) {
  const m = purchase.monthly;
  return (
    <Breakdown
      slices={[
        { label: "Loan", value: m.payment, color: MONTHLY[0] },
        { label: "Loan insurance", value: m.insurance, color: MONTHLY[1] },
        { label: "Service charges", value: m.serviceCharges, color: MONTHLY[2] },
        { label: "Taxe foncière", value: m.propertyTax, color: MONTHLY[3] },
        { label: "Home insurance", value: m.homeInsurance, color: MONTHLY[4] },
      ]}
    />
  );
}

export function FeeBreakdown({
  purchase,
  agencyFees,
}: {
  purchase: Purchase;
  agencyFees: number;
}) {
  const n = purchase.notary;
  return (
    <Breakdown
      slices={[
        { label: "Transfer duties", value: n.duties, color: FEES[0] },
        { label: "Notary's fee, VAT in", value: n.emoluments, color: FEES[1] },
        { label: "Loan guarantee", value: purchase.guaranteeFee, color: FEES[2] },
        {
          label: "Registry + filings",
          value: n.csi + n.disbursements,
          color: FEES[3],
        },
        { label: "Arrangement fee", value: purchase.arrangementFee, color: FEES[4] },
        {
          label: "Agency + broker",
          value: agencyFees + purchase.brokerFee,
          color: FEES[5],
        },
      ]}
    />
  );
}

/** Monthly, to stay in the same unit as the card's own summary. */
export function RunningCostBreakdown({ purchase }: { purchase: Purchase }) {
  const m = purchase.monthly;
  return (
    <Breakdown
      slices={[
        { label: "Service charges", value: m.serviceCharges, color: RUNNING[0] },
        { label: "Taxe foncière", value: m.propertyTax, color: RUNNING[1] },
        { label: "Home insurance", value: m.homeInsurance, color: RUNNING[2] },
      ]}
    />
  );
}
